import React from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import { ButtonComponent, type ButtonComponentProps } from './ButtonComponent';

export interface AddButtonProps extends Omit<ButtonComponentProps, 'children' | 'startIcon'> {
  text?: string;
}

/**
 * AddButton - Reusable Add Button Component
 *
 * A specialized button component for "Add" actions that provides:
 * - Pre-configured Add icon (plus icon)
 * - Customizable text (defaults to "Add New")
 * - Built on top of ButtonComponent for consistency
 * - Supports all ButtonComponent props except children and startIcon
 * - Perfect for create/add functionality across the application
 */
export const AddButton: React.FC<AddButtonProps> = ({
  text = "Add New",
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  ...props
}) => {
  return (
    <ButtonComponent
      variant={variant}
      color={color}
      size={size}
      startIcon={<AddIcon />}
      sx={{
        backgroundColor: "#1d4ed8", // primary-700
        color: "#fff",              // text-white
        "&:hover": {
          backgroundColor: "#1e40af", // primary-800
          transform: 'translateY(-1px)',
          boxShadow: variant === 'contained' ? 2 : 0,
        },
        "&:focus": {
          outline: "none",
          boxShadow: "0 0 0 4px #93c5fd", // focus:ring-4 focus:ring-primary-300          
        },
      }}
      {...props}
    >
      {text}
    </ButtonComponent>
  );
};

export default AddButton;